/**
 * HRN factor input — 4 dropdowns (LO / FE / DPH / NP) + auto-calculated HRN
 * value + auto-coloured band pill.
 *
 * Used on both the Initial and Residual columns of a hazard row. The user
 * never types numbers — they pick from named scale options, which keeps
 * stored values within the canonical HRN scale and prevents typos.
 *
 * Props:
 *   - values: { lo, fe, dph, np }
 *   - onChange: (next) => void   // called with the full new values object
 *   - disabled?: boolean         // read-only mode
 *   - compact?: boolean          // smaller layout for tight tables
 *   - comparedTo?: number        // optional baseline HRN (typically the Initial value).
 *                                 When provided AND the current value is lower,
 *                                 the pill appends "— N% reduction" so users see
 *                                 how much their controls cut the risk.
 *   - testIdPrefix?: string      // for Playwright/Jest selectors
 */
import React from 'react';
import { LO_SCALE, FE_SCALE, DPH_SCALE, NP_SCALE, BAND_CLASSES, computeHrn, bandFor, formatHrnPill, needsReduction } from '../constants/riskAssessment';

const FIELDS = [
  { key: 'lo',  label: 'LO',  scale: LO_SCALE,  title: 'Likelihood of Occurrence' },
  { key: 'fe',  label: 'FE',  scale: FE_SCALE,  title: 'Frequency of Exposure' },
  { key: 'dph', label: 'DPH', scale: DPH_SCALE, title: 'Degree of Possible Harm' },
  { key: 'np',  label: 'NP',  scale: NP_SCALE,  title: 'Number of Persons at risk' },
];

export default function HrnInput({ values, onChange, disabled, compact, comparedTo, testIdPrefix }) {
  const lo  = Number(values?.lo)  || 0;
  const fe  = Number(values?.fe)  || 0;
  const dph = Number(values?.dph) || 0;
  const np  = Number(values?.np)  || 0;
  const hrn = computeHrn(lo, fe, dph, np);
  const band = bandFor(hrn);
  // Rounding rule mirrored from formatHrnPill so the inline equation result
  // exactly matches the verdict pill.
  const roundedHrn = hrn < 10 ? Math.round(hrn * 100) / 100 : Math.round(hrn);

  // Build the pill label. When the caller passes a baseline (comparedTo) AND
  // the current HRN is lower, we show "— N% reduction" instead of (or as well
  // as) the "Reduction recommended" hint, so the user sees how much their
  // controls have cut the risk.
  const baseline = Number(comparedTo) || 0;
  let pillLabel;
  if (baseline > 0 && hrn < baseline) {
    const reductionPct = Math.round(((baseline - hrn) / baseline) * 100);
    const stillNeedsMore = needsReduction(hrn);
    pillLabel = `${roundedHrn} · ${band.label} — ${reductionPct}% reduction${stillNeedsMore ? ', more recommended' : ''}`;
  } else {
    pillLabel = formatHrnPill(hrn);
  }

  const handleChange = (key, raw) => {
    if (disabled) return;
    onChange({ ...values, [key]: Number(raw) });
  };

  const selectClass =
    `block w-full ${compact ? 'text-[11px] py-0.5 pl-1.5 pr-6' : 'text-xs py-1 pl-2 pr-7'} ` +
    'rounded border border-gray-300 dark:border-gray-600 ' +
    'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ' +
    'focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 ' +
    'disabled:cursor-not-allowed disabled:opacity-60';

  return (
    <div className={`space-y-1 ${compact ? 'min-w-[9rem]' : 'min-w-[11rem]'}`}>
      {FIELDS.map((f) => {
        const v = Number(values?.[f.key]) || 0;
        const testId = testIdPrefix ? `${testIdPrefix}-${f.key}` : undefined;
        return (
          <div key={f.key} className="flex items-center gap-1.5">
            <label
              className={`${compact ? 'text-[10px]' : 'text-[11px]'} font-semibold text-gray-500 dark:text-gray-400 w-7 shrink-0`}
              title={f.title}
            >
              {f.label}
            </label>
            <select
              value={v}
              onChange={(e) => handleChange(f.key, e.target.value)}
              disabled={disabled}
              className={selectClass}
              data-testid={testId}
            >
              {/* If a stored value is not on the scale, show it as an extra option to avoid silent data loss. */}
              {!f.scale.some((opt) => opt.v === v) && (
                <option value={v}>{v} · (off-scale)</option>
              )}
              {f.scale.map((opt) => (
                <option key={opt.v} value={opt.v}>{opt.l} ({opt.v})</option>
              ))}
            </select>
          </div>
        );
      })}
      <div className="flex items-center justify-center pt-1">
        <span
          className={`inline-block px-3 py-1 rounded text-sm font-semibold ${BAND_CLASSES[band.tone]}`}
          title={`HRN = ${lo} × ${fe} × ${dph} × ${np}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-hrn` : undefined}
        >
          {pillLabel}
        </span>
      </div>
      {/* Inline HRN equation — the visible working underneath the verdict pill.
          Hidden until all four factors are scored so we don't render
          "0 × 0 × 0 × 0 = 0" while the user is mid-edit. */}
      {hrn > 0 && (
        <div className="flex items-center justify-center pt-1">
          <span
            className={`${compact ? 'text-xs' : 'text-sm'} font-mono tabular-nums text-gray-600 dark:text-gray-400`}
            data-testid={testIdPrefix ? `${testIdPrefix}-hrn-equation` : undefined}
          >
            HRN = {lo} × {fe} × {dph} × {np} = {roundedHrn}
          </span>
        </div>
      )}
    </div>
  );
}
